import { useSelectMoveableFrame } from '../../selectors';
import { closeMoveableFrame, maximizeMoveableFrame, minimizeMoveableFrame, restoreMoveableFrame, saveMoveableFrameStyle, } from '../../actions';
import CloseIcon from '@mui/icons-material/Close';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import HelpIcon from '@mui/icons-material/Help';
import MinimizeIcon from '@mui/icons-material/Minimize';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { Box, IconButton, ListItemIcon, ListItemText, MenuItem, } from '@mui/material';
import React, { useEffect, useRef, useState, } from 'react';
import { useDispatch } from 'react-redux';
import { HeaderBar, HeaderButtons, HeaderText, StyledMenu, StyledMovable, TargetDiv, } from './MoveableFrame.style';
import { FrameStatus } from '../../types/moveableFrame.types';
const MoveableFrame = ({ id, children, title, color, menuItems, titleIcon, }) => {
    const { BOUNDS, frameStatus, MIN_HEIGHT, MIN_WIDTH, newFrameStyle } = useSelectMoveableFrame();
    const dispatch = useDispatch();
    const [moveableTarget, setMoveableTarget] = useState(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const targetRef = useRef(null);
    useEffect(() => {
        frameStatus === FrameStatus.OPENED
            ? setTimeout(() => {
                setMoveableTarget(targetRef);
            }, 100)
            : setMoveableTarget(undefined);
    }, [frameStatus]);
    useEffect(() => {
        const style = targetRef.current?.style;
        if (newFrameStyle && style) {
            style.setProperty('width', newFrameStyle.width ?? 'auto');
            style.setProperty('height', newFrameStyle.height ?? 'auto');
            newFrameStyle.transform
                ? style.setProperty('transform', newFrameStyle.transform)
                : style.removeProperty('transform');
            newFrameStyle.inset
                ? style.setProperty('inset', newFrameStyle.inset)
                : style.removeProperty('inset');
        }
    }, [newFrameStyle, targetRef.current?.style]);
    const handleMinimize = () => {
        dispatch(minimizeMoveableFrame());
    };
    const handleMaximize = () => {
        dispatch(maximizeMoveableFrame());
    };
    const handleRestoreFrame = () => {
        dispatch(restoreMoveableFrame());
    };
    const handleMenuClick = (event) => {
        setMenuAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setMenuAnchorEl(null);
    };
    const Header = (React.createElement(HeaderBar, null,
        React.createElement(HeaderText, null,
            titleIcon ?? React.createElement(HelpIcon, { fontSize: "small", color: "secondary" }),
            React.createElement(Box, { component: "span", sx: { fontWeight: 700, fontSize: '12px', margin: '0 4px' } }, title),
            menuItems && frameStatus !== FrameStatus.MINIMIZED && (React.createElement(React.Fragment, null,
                React.createElement(IconButton, { "data-cy": "frame-menu", "aria-label": "more", "aria-controls": "long-menu", "aria-haspopup": "true", size: "small", sx: { margin: '0 4px' }, onClick: handleMenuClick },
                    React.createElement(MoreHorizIcon, { fontSize: "inherit", color: "secondary" })),
                React.createElement(StyledMenu, { anchorEl: menuAnchorEl, open: Boolean(menuAnchorEl), onClose: handleMenuClose, anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                    } }, menuItems.map(item => (React.createElement(MenuItem, { dense: true, onClick: e => {
                        item.onClick(e);
                        handleMenuClose();
                    }, key: item.key, "data-cy": item?.dataCy ?? item.key },
                    item?.icon && (React.createElement(ListItemIcon, null, item.icon)),
                    React.createElement(ListItemText, null, item.text)))))))),
        React.createElement(HeaderButtons, null,
            frameStatus === FrameStatus.MINIMIZED && (React.createElement(React.Fragment, null,
                React.createElement(IconButton, { size: "small", sx: { margin: '0 2px' }, onClick: handleRestoreFrame },
                    React.createElement(CropSquareIcon, { fontSize: "inherit", color: "secondary" })),
                React.createElement(IconButton, { size: "small", sx: { margin: '0 2px' }, onClick: handleMaximize },
                    React.createElement(OpenInFullIcon, { fontSize: "inherit", color: "secondary" })))),
            frameStatus === FrameStatus.OPENED && (React.createElement(React.Fragment, null,
                React.createElement(IconButton, { size: "small", sx: { margin: '0 2px' }, onClick: handleMinimize },
                    React.createElement(MinimizeIcon, { fontSize: "inherit", color: "secondary" })),
                React.createElement(IconButton, { size: "small", sx: { margin: '0 2px' }, onClick: handleMaximize },
                    React.createElement(OpenInFullIcon, { fontSize: "inherit", color: "secondary" })))),
            frameStatus === FrameStatus.MAXIMIZED && (React.createElement(React.Fragment, null,
                React.createElement(IconButton, { size: "small", sx: { margin: '0 2px' }, onClick: handleMinimize },
                    React.createElement(MinimizeIcon, { fontSize: "inherit", color: "secondary" })),
                React.createElement(IconButton, { size: "small", sx: { margin: '0 2px' }, onClick: handleRestoreFrame },
                    React.createElement(CloseFullscreenIcon, { fontSize: "inherit", color: "secondary" })))),
            React.createElement(IconButton, { size: "small", sx: { margin: '0 2px' }, onClick: () => {
                    dispatch(closeMoveableFrame());
                } },
                React.createElement(CloseIcon, { fontSize: "inherit", color: "secondary" })))));
    const saveCurrentStyle = () => {
        const style = targetRef.current?.style;
        if (style) {
            dispatch(saveMoveableFrameStyle({
                width: style.width,
                height: style.height,
                transform: style.transform,
                inset: style.inset,
            }));
        }
    };
    if (frameStatus === FrameStatus.CLOSED) {
        return null;
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(TargetDiv, { ref: targetRef, id: id ?? 'movable-frame', "data-cy": id ?? 'movable-frame', "$minWidth": MIN_WIDTH, "$minHeight": MIN_HEIGHT },
            Header,
            children),
        React.createElement(StyledMovable, { target: moveableTarget, "$color": color, draggable: true, resizable: true, snappable: true, edge: true, origin: false, renderDirections: ['nw', 'ne', 'sw', 'se'], bounds: BOUNDS, onDragStart: e => {
                if (['input', 'select', 'p'].indexOf(e.inputEvent.target.tagName.toLowerCase()) > -1) {
                    e.stopDrag();
                }
            }, onDrag: e => {
                e.target.style.transform = e.transform;
            }, onResize: e => {
                e.target.style.width = `${e.width}px`;
                e.target.style.height = `${e.height}px`;
                e.target.style.transform = e.drag.transform;
            }, onDragEnd: saveCurrentStyle, onResizeEnd: saveCurrentStyle })));
};
export default MoveableFrame;
